import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import ProtectedRoute from '@components/ProtectedRoute';
import Button from '@components/Button';
import Modal from '@components/Modal';
import { supabase } from '@lib/supabase';
import { useAuth } from '@lib/useAuth';
import { useUserRole } from '@lib/useUserRole';
import { signOut } from '@lib/auth';
import {
  createAndStorePushSubscription,
  isIosSafari,
  isStandalonePwa,
} from '@lib/pushNotifications';

const DAYS = [
  { label: 'Su', value: 'Sun', full: 'Sunday' },
  { label: 'Mo', value: 'Mon', full: 'Monday' },
  { label: 'Tu', value: 'Tue', full: 'Tuesday' },
  { label: 'We', value: 'Wed', full: 'Wednesday' },
  { label: 'Th', value: 'Thu', full: 'Thursday' },
  { label: 'Fr', value: 'Fri', full: 'Friday' },
  { label: 'Sa', value: 'Sat', full: 'Saturday' },
];

const DAY_VALUES = DAYS.map((day) => day.value);
const DEFAULT_REPEAT_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const WEEKDAY_VALUES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];

const normalizeDaySelection = (days: string[]): string[] => {
  const unique = new Set(days);
  return DAY_VALUES.filter((value) => unique.has(value));
};

const hasSameMembers = (selection: string[], expected: string[]): boolean => {
  if (selection.length !== expected.length) return false;
  return expected.every((value) => selection.includes(value));
};

const formatReminderDays = (days?: string[] | null): string | null => {
  if (!Array.isArray(days) || days.length === 0) return null;
  return normalizeDaySelection(days).join(', ');
};

const DEFAULT_REMINDER_MESSAGE =
  'Time to reset your mind. Take a minute to breathe.';

const deriveProfileNameFromDisplay = (name: string): string =>
  name.replace(/\s+/g, '');

const DEFAULT_REMINDER_TIME =
  (import.meta.env.VITE_SETTINGS_EMAIL_TIME as string | undefined) || '08:00';

const DANGER_ZONE_API_BASE = (() => {
  const envUrl = (import.meta.env.VITE_DANGER_ZONE_API_BASE ?? '').trim();
  if (envUrl) {
    return envUrl.replace(/\/$/, '');
  }
  // Default to same-origin serverless endpoints (Vercel dev/prod) when no override is provided.
  if (typeof window !== 'undefined') {
    return `${window.location.origin}/api`;
  }
  return '/api';
})();

const readPersistedTab = (): 'Account' | 'Profile' | 'Breathing Reminders' => {
  if (typeof window === 'undefined') {
    return 'Account';
  }

  const stored = sessionStorage.getItem('settings-active-tab');
  if (
    stored === 'Account' ||
    stored === 'Profile' ||
    stored === 'Breathing Reminders'
  ) {
    return stored as any;
  }

  return 'Account';
};

type DangerZoneAction = 'delete-data' | 'delete-account';

interface BadgeRequirement {
  id?: string | null;
  name?: string | null;
  image_url?: string | null;
  color?: string | null;
}

interface BadgeFeatureRequirementRow {
  badges?: BadgeRequirement | null;
}

const formatAiProfileUnlockMessage = (badgeNames: string[]): string => {
  if (!badgeNames || badgeNames.length === 0) {
    return 'Earn the required badge to unlock AI profile picture generation.';
  }

  if (badgeNames.length === 1) {
    return `Earn the "${badgeNames[0]}" badge to unlock AI profile picture generation.`;
  }

  if (badgeNames.length === 2) {
    return `Earn the "${badgeNames[0]}" or "${badgeNames[1]}" badge to unlock AI profile picture generation.`;
  }

  const formatted = badgeNames.map((name) => `"${name}"`);
  const last = formatted.pop();
  return `Earn one of these badges to unlock AI profile picture generation: ${formatted.join(', ')}, or ${last}.`;
};

const buildBadgeAssetUrl = (value?: string | null) => {
  if (!value) return null;
  const trimmed = String(value).trim();
  if (trimmed.startsWith('http')) return trimmed;
  const supabaseUrl = (import.meta.env.VITE_SUPABASE_URL || '').replace(
    /\/$/,
    ''
  );
  const normalizedPath = trimmed.replace(/^\/+/, '');
  return `${supabaseUrl}/storage/v1/object/public/${encodeURI(normalizedPath)}`;
};

export default function Settings() {
  const location = useLocation();

  const [active, setActive] = useState<
    'Account' | 'Profile' | 'Breathing Reminders'
  >(readPersistedTab());
  const { user, session, userFeatures } = useAuth();
  const navigate = useNavigate();
  const sessionAccessToken = session?.access_token ?? null;
  const { role } = useUserRole(user?.id ?? null);
  const isAdmin = role === 'admin';
  const hasAiProfilePicture = userFeatures.includes('ai_profile_picture');
  const hasProfileBio = userFeatures.includes('profile_bio');
  const canUseAiProfilePicture = hasAiProfilePicture || isAdmin;

  useEffect(() => {
    if (typeof window === 'undefined') return;
    sessionStorage.removeItem('settings-active-tab');
    try {
      const hash = (location.hash || '').replace(/^#/, '').toLowerCase();
      const params = new URLSearchParams(location.search);
      const tabParam = (params.get('tab') || '').toLowerCase();

      const source = hash || tabParam;
      if (source) {
        if (source === 'account') setActive('Account');
        else if (source === 'profile') setActive('Profile');
        else if (source === 'reminders' || source === 'breathing-reminders')
          setActive('Breathing Reminders');
      }
    } catch (e) {
      // ignore
    }
  }, [location]);

  const [displayProfile, setDisplayProfile] = useState<boolean>(false);
  const [displayName, setDisplayName] = useState<string>('');
  const [profileBio, setProfileBio] = useState<string>('');
  const [profileName, setProfileName] = useState<string>('');
  const [profilePictureName, setProfilePictureName] = useState<string | null>(
    null
  );
  const [profilePictureGeneratedAt, setProfilePictureGeneratedAt] = useState<
    string | null
  >(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  const [updatingPassword, setUpdatingPassword] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState<string | null>(null);
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [generatingAvatar, setGeneratingAvatar] = useState(false);
  const [aiProfileBadgeRequirements, setAiProfileBadgeRequirements] = useState<
    BadgeRequirement[]
  >([]);
  const [showAiProfileLockedModal, setShowAiProfileLockedModal] =
    useState(false);
  const [previewBadge, setPreviewBadge] = useState<BadgeRequirement | null>(
    null
  );
  const [profileBioBadgeRequirements, setProfileBioBadgeRequirements] =
    useState<BadgeRequirement[]>([]);
  const [showProfileBioLockedModal, setShowProfileBioLockedModal] =
    useState(false);
  const jobOptions = [
    'Software Engineer',
    'Designer',
    'Product Manager',
    'Teacher',
    'Trader',
    'Doctor',
    'Nurse',
    'Chef',
    'Photographer',
    'Artist',
    'Writer',
    'Lawyer',
    'Accountant',
    'Marketing Manager',
    'Salesperson',
    'Data Scientist',
    'Mechanical Engineer',
    'Electrician',
    'Architect',
    'UX Designer',
    'Researcher',
  ];

  const animalOptions = [
    'Dog',
    'Cat',
    'Horse',
    'Fox',
    'Owl',
    'Wolf',
    'Tiger',
    'Lion',
    'Bear',
    'Bull',
    'Rabbit',
    'Deer',
    'Dolphin',
    'Whale',
    'Penguin',
    'Parrot',
    'Eagle',
    'Panda',
    'Koala',
    'Squirrel',
    'Frog',
  ];

  const [selectedJob, setSelectedJob] = useState<string>('');
  const [selectedAnimal, setSelectedAnimal] = useState<string>('');
  const aiProfileBadgeNames = useMemo(
    () =>
      aiProfileBadgeRequirements
        .map((badge) => badge?.name)
        .filter((name): name is string => Boolean(name)),
    [aiProfileBadgeRequirements]
  );
  const aiProfileUnlockMessage = useMemo(
    () => formatAiProfileUnlockMessage(aiProfileBadgeNames),
    [aiProfileBadgeNames]
  );

  const profileBioBadgeNames = useMemo(
    () =>
      profileBioBadgeRequirements
        .map((badge) => badge?.name)
        .filter((name): name is string => Boolean(name)),
    [profileBioBadgeRequirements]
  );

  const profileBioUnlockMessage = useMemo(() => {
    if (!profileBioBadgeNames || profileBioBadgeNames.length === 0) {
      return 'Earn the required badge to unlock your profile bio feature.';
    }

    if (profileBioBadgeNames.length === 1) {
      return `Earn the "${profileBioBadgeNames[0]}" badge to unlock your profile bio.`;
    }

    if (profileBioBadgeNames.length === 2) {
      return `Earn the "${profileBioBadgeNames[0]}" or "${profileBioBadgeNames[1]}" badge to unlock your profile bio.`;
    }

    const formatted = profileBioBadgeNames.map((name) => `"${name}"`);
    const last = formatted.pop();
    return `Earn one of these badges to unlock your profile bio: ${formatted.join(', ')}, or ${last}.`;
  }, [profileBioBadgeNames]);

  const defaultTimezone = useMemo(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    } catch {
      return 'UTC';
    }
  }, []);

  const [remindersEnabled, setRemindersEnabled] = useState<boolean>(false);
  const [reminders, setReminders] = useState<
    {
      id: string;
      enabled: boolean;
      frequency: 'daily' | 'weekly' | 'weekdays' | 'custom' | 'once';
      days: string[] | null;
      time: string;
      notification_type: 'push' | 'email' | 'both';
      message: string | null;
      timezone?: string | null;
    }[]
  >([]);
  const [editingReminderId, setEditingReminderId] = useState<string | null>(
    null
  );
  const [editingReminderEnabled, setEditingReminderEnabled] =
    useState<boolean>(true);
  const [isRepeat, setIsRepeat] = useState(true);
  const [reminderDays, setReminderDays] = useState<string[]>([
    ...DEFAULT_REPEAT_DAYS,
  ]);
  const [reminderTime, setReminderTime] = useState<string>(
    DEFAULT_REMINDER_TIME
  );
  const [reminderNotificationType, setReminderNotificationType] = useState<
    'push' | 'email' | 'both'
  >('push');
  const [reminderMessage, setReminderMessage] = useState<string>(
    DEFAULT_REMINDER_MESSAGE
  );
  const [reminderTimezone, setReminderTimezone] =
    useState<string>(defaultTimezone);
  const detectBrowserTimezone = useCallback(() => {
    try {
      if (
        typeof Intl !== 'undefined' &&
        typeof Intl.DateTimeFormat === 'function'
      ) {
        const resolved =
          Intl.DateTimeFormat().resolvedOptions().timeZone || defaultTimezone;
        return resolved;
      }
    } catch {
      // ignore and fall back below
    }
    return defaultTimezone;
  }, [defaultTimezone]);
  const [browserTimezone, setBrowserTimezone] =
    useState<string>(defaultTimezone);
  const [reminderSaving, setReminderSaving] = useState(false);
  const [reminderError, setReminderError] = useState<string | null>(null);
  const [reminderSavedBanner, setReminderSavedBanner] = useState(false);
  const [reminderModalOpen, setReminderModalOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [dangerModal, setDangerModal] = useState<DangerZoneAction | null>(null);
  const [dangerZoneLoading, setDangerZoneLoading] =
    useState<DangerZoneAction | null>(null);
  const [dangerZoneError, setDangerZoneError] = useState<string | null>(null);
  const [dangerZoneSuccess, setDangerZoneSuccess] = useState<string | null>(
    null
  );
  const reminderModalCloseTimeout = useRef<number | null>(null);
  const [togglingReminderIds, setTogglingReminderIds] = useState<string[]>([]);
  // debug logs removed
  const [notificationSupported, setNotificationSupported] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState<
    NotificationPermission | 'default' | 'denied' | 'granted'
  >('default');
  const [notificationSetupLoading, setNotificationSetupLoading] =
    useState(false);
  const [notificationStatusMessage, setNotificationStatusMessage] = useState<
    string | null
  >(null);
  const [iosNeedsPwa, setIosNeedsPwa] = useState(false);
  // push debug info removed

  useEffect(() => {
    const tz = detectBrowserTimezone();
    setBrowserTimezone(tz);
  }, [detectBrowserTimezone]);

  useEffect(() => {
    let active = true;
    async function loadAiProfileRequirements() {
      try {
        const { data: featureRow, error: featureError } = await supabase
          .from('features')
          .select('id')
          .eq('key', 'ai_profile_picture')
          .maybeSingle();

        if (featureError) throw featureError;

        if (!featureRow?.id) {
          if (active) {
            setAiProfileBadgeRequirements([]);
          }
          return;
        }

        const { data: badgeRows, error: badgeError } = await supabase
          .from('badge_features')
          .select(
            `
              badges (
                id,
                name,
                image_url,
                color
              )
            `
          )
          .eq('feature_id', featureRow.id);

        if (badgeError) throw badgeError;

        const rows =
          (badgeRows as BadgeFeatureRequirementRow[] | null | undefined)
            ?.map((row) => row?.badges)
            .filter((badge): badge is BadgeRequirement => Boolean(badge)) ?? [];

        if (active) {
          setAiProfileBadgeRequirements(rows);
        }
      } catch (err) {
        console.debug('Failed to load AI profile picture requirements', err);
        if (active) {
          setAiProfileBadgeRequirements([]);
        }
      }
    }

    loadAiProfileRequirements();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;

    async function loadProfileBioRequirements() {
      try {
        const { data: featureRow, error: featureError } = await supabase
          .from('features')
          .select('id')
          .eq('key', 'profile_bio')
          .maybeSingle();

        if (featureError) throw featureError;

        if (!featureRow?.id) {
          if (active) setProfileBioBadgeRequirements([]);
          return;
        }

        const { data: badgeRows, error: badgeError } = await supabase
          .from('badge_features')
          .select(
            `
              badges (
                id,
                name,
                image_url,
                color
              )
            `
          )
          .eq('feature_id', featureRow.id);

        if (badgeError) throw badgeError;

        const rows =
          (badgeRows as BadgeFeatureRequirementRow[] | null | undefined)
            ?.map((row) => row?.badges)
            .filter((badge): badge is BadgeRequirement => Boolean(badge)) ?? [];

        if (active) setProfileBioBadgeRequirements(rows);
      } catch (err) {
        console.debug('Failed to load profile bio requirements', err);
        if (active) setProfileBioBadgeRequirements([]);
      }
    }

    loadProfileBioRequirements();

    return () => {
      active = false;
    };
  }, []);

  const resetReminderFormFields = useCallback(() => {
    setEditingReminderId(null);
    setEditingReminderEnabled(true);
    setIsRepeat(true);
    setReminderDays([...DEFAULT_REPEAT_DAYS]);
    setReminderTime(DEFAULT_REMINDER_TIME);
    setReminderNotificationType('push');
    setReminderMessage(DEFAULT_REMINDER_MESSAGE);
    setReminderTimezone(browserTimezone);
  }, [browserTimezone]);

  const toggleReminderDay = useCallback(
    (dayValue: string) => {
      if (!isRepeat) return;
      setReminderDays((prev) => {
        const next = prev.includes(dayValue)
          ? prev.filter((value) => value !== dayValue)
          : [...prev, dayValue];
        return normalizeDaySelection(next);
      });
    },
    [isRepeat]
  );

  const clearReminderModalTimer = useCallback(() => {
    if (reminderModalCloseTimeout.current) {
      clearTimeout(reminderModalCloseTimeout.current);
      reminderModalCloseTimeout.current = null;
    }
  }, []);

  const closeReminderModal = useCallback(() => {
    clearReminderModalTimer();
    setReminderModalOpen(false);
    setReminderError(null);
    setReminderSavedBanner(false);
    resetReminderFormFields();
  }, [clearReminderModalTimer, resetReminderFormFields]);

  useEffect(() => {
    if (reminderModalOpen) {
      setReminderTimezone(browserTimezone);
    }
  }, [browserTimezone, reminderModalOpen]);

  const ensurePushPermission = useCallback(async () => {
    if (!user) {
      setReminderError('You must be signed in to enable push reminders.');
      return false;
    }
    if (!notificationSupported) {
      setReminderError(
        'This browser does not support push notifications. Try a different browser.'
      );
      return false;
    }

    if (isIosSafari() && !isStandalonePwa()) {
      setReminderError(
        'On iPhone/iPad Safari, add Xhalin to your Home Screen to enable push reminders.'
      );
      return false;
    }

    setNotificationSetupLoading(true);
    setNotificationStatusMessage(null);
    try {
      const result = await createAndStorePushSubscription(user.id);
      setNotificationPermission(result.permission ?? Notification.permission);
      if (result.permission !== 'granted') {
        setReminderError(
          'Push notifications are blocked. Use the lock icon in your browser to allow them.'
        );
        return false;
      }
      setNotificationStatusMessage('Push notifications are ready.');
      return true;
    } catch (err: any) {
      setReminderError(
        err?.message || 'Unable to register for push notifications.'
      );
      return false;
    } finally {
      setNotificationSetupLoading(false);
    }
  }, [notificationSupported, user]);

  const displayProfileSelectClasses =
    'w-full rounded border px-3 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 ' +
    (displayProfile
      ? 'border-emerald-500 bg-emerald-50 text-emerald-700 focus:ring-emerald-200'
      : 'border-rose-500 bg-rose-50 text-rose-700 focus:ring-rose-200');
  const trimmedProfileName = profileName.trim();
  const profileLinkPath =
    displayProfile && trimmedProfileName
      ? `/profile/${trimmedProfileName}`
      : null;
  useEffect(() => {
    async function load() {
      if (!user) return;
      try {
        const { data, error } = await supabase
          .from('user_settings')
          .select('*')
          .eq('user_id', user.id)
          .limit(1)
          .single();

        if (error && (error as any).code !== 'PGRST116') {
          // ignore no rows but surface other errors
          console.error(error);
        }

        if (data) {
          setDisplayProfile(Boolean(data.display_profile));
          setDisplayName(data.display_name ?? '');
          setProfileBio(data.profile_bio ?? '');
          setProfileName(data.profile_name ?? '');
          setProfilePictureName(data.profile_picture_name ?? null);
          setProfilePictureGeneratedAt(
            data.profile_picture_generated_at ?? null
          );
        }
      } catch (err) {
        console.error('Failed to load settings', err);
      }
    }

    load();
  }, [user]);

  useEffect(() => {
    if (!displayName) return;
    if (profileName) return;
    const derived = deriveProfileNameFromDisplay(displayName);
    if (!derived) return;
    setProfileName(derived);
  }, [displayName, profileName]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const hasNotification =
      typeof window !== 'undefined' && 'Notification' in window;
    setNotificationSupported(hasNotification);
    if (hasNotification) {
      try {
        // eslint-disable-next-line no-new
        setNotificationPermission((Notification as any).permission);
      } catch {
        setNotificationPermission('default');
      }
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    setIosNeedsPwa(isIosSafari() && !isStandalonePwa());
  }, []);

  useEffect(() => {
    // push/service worker debug effect removed
  }, [remindersEnabled]);

  // reminder debug details removed

  const refreshReminders = useCallback(async () => {
    if (!user) {
      setReminders([]);
      setRemindersEnabled(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('breathing_reminders')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: true });

      if (error && (error as any).code !== 'PGRST116') {
        console.error(error);
      }

      const list = Array.isArray(data) ? data : [];
      setReminders(
        list.map((r: any) => ({
          id: r.id as string,
          enabled: Boolean(r.enabled),
          frequency:
            r.frequency === 'weekly' ||
            r.frequency === 'weekdays' ||
            r.frequency === 'custom' ||
            r.frequency === 'once'
              ? r.frequency
              : 'daily',
          days: Array.isArray(r.days) ? (r.days as string[]) : null,
          time:
            typeof r.time === 'string' && r.time
              ? r.time
              : DEFAULT_REMINDER_TIME,
          notification_type:
            r.notification_type === 'email' || r.notification_type === 'both'
              ? r.notification_type
              : 'push',
          message:
            typeof r.message === 'string' && r.message
              ? r.message
              : DEFAULT_REMINDER_MESSAGE,
          timezone: typeof r.timezone === 'string' ? r.timezone : null,
        }))
      );
      const anyEnabled = list.some((reminder: any) =>
        Boolean(reminder.enabled)
      );
      setRemindersEnabled((prev) => prev || anyEnabled);
    } catch (err) {
      console.error('Failed to load breathing reminders', err);
    }
  }, [user]);

  useEffect(() => {
    refreshReminders();
  }, [refreshReminders]);

  useEffect(() => {
    return () => {
      clearReminderModalTimer();
    };
  }, [clearReminderModalTimer]);

  const handleReminderToggle = useCallback(async () => {
    if (!user) {
      setReminderError('You need to be signed in to enable reminders.');
      return;
    }

    setReminderError(null);

    if (remindersEnabled) {
      // disabling: update existing reminders server-side so state persists
      setRemindersEnabled(false);
      try {
        await supabase
          .from('breathing_reminders')
          .update({ enabled: false, updated_at: new Date().toISOString() })
          .eq('user_id', user.id);
      } catch (err) {
        console.error('Failed to disable reminders server-side', err);
      }
      return;
    }

    const granted = await ensurePushPermission();
    if (!granted) {
      setRemindersEnabled(false);
      return;
    }

    // push permission granted, enabling reminders
    setReminderTimezone(browserTimezone);
    setRemindersEnabled(true);
    try {
      // If the user already has reminders, enable them. If not, insert a default reminder
      const { data: existing, error: fetchError } = await supabase
        .from('breathing_reminders')
        .select('id')
        .eq('user_id', user.id)
        .limit(1);

      if (fetchError) {
        console.error('Failed to check existing reminders', fetchError);
      }

      if (existing && existing.length > 0) {
        await supabase
          .from('breathing_reminders')
          .update({ enabled: true, updated_at: new Date().toISOString() })
          .eq('user_id', user.id);
      } else {
        // create a minimal default reminder so the "enabled" state is persisted
        try {
          await supabase.from('breathing_reminders').insert({
            user_id: user.id,
            enabled: true,
            frequency: 'daily',
            days: null,
            time: reminderTime || DEFAULT_REMINDER_TIME,
            notification_type: 'push',
            message: DEFAULT_REMINDER_MESSAGE,
            timezone: browserTimezone || defaultTimezone,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          });
        } catch (err) {
          console.error('Failed to create default reminder', err);
        }
      }

      // refresh client-side list to reflect persisted state
      await refreshReminders();
    } catch (err) {
      console.error('Failed to enable reminders server-side', err);
    }
  }, [
    browserTimezone,
    ensurePushPermission,
    notificationSupported,
    remindersEnabled,
    user,
  ]);

  const toggleReminderEnabled = useCallback(
    async (reminderId: string, currentlyEnabled: boolean) => {
      if (!user) {
        setReminderError('You need to be signed in to update reminders.');
        return;
      }

      setReminderError(null);
      setTogglingReminderIds((prev) => [...prev, reminderId]);

      try {
        const { error } = await supabase
          .from('breathing_reminders')
          .update({
            enabled: !currentlyEnabled,
            updated_at: new Date().toISOString(),
          })
          .eq('id', reminderId)
          .eq('user_id', user.id);

        if (error) {
          throw error;
        }

        await refreshReminders();
      } catch (err: any) {
        console.error('Failed to update reminder status', err);
        setReminderError(
          err?.message || 'Unable to pause or resume that reminder.'
        );
      } finally {
        setTogglingReminderIds((prev) =>
          prev.filter((value) => value !== reminderId)
        );
      }
    },
    [refreshReminders, user]
  );

  const handleDangerZoneAction = useCallback(
    async (action: DangerZoneAction) => {
      if (!user) {
        setDangerZoneError('You must be signed in to perform that action.');
        return;
      }

      if (!sessionAccessToken) {
        setDangerZoneError(
          'Session expired. Please sign in again and try once more.'
        );
        return;
      }

      setDangerZoneLoading(action);
      setDangerZoneError(null);
      setDangerZoneSuccess(null);

      const endpoint =
        action === 'delete-data'
          ? `${DANGER_ZONE_API_BASE}/delete-data`
          : `${DANGER_ZONE_API_BASE}/delete-account`;

      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${sessionAccessToken}`,
          },
        });

        let payload: any = null;
        try {
          payload = await response.json();
        } catch {
          payload = null;
        }

        if (!response.ok) {
          const message =
            payload?.error ||
            payload?.message ||
            'Unable to complete that request.';
          throw new Error(message);
        }

        if (action === 'delete-account') {
          setDangerZoneSuccess('Your account has been deleted.');
          try {
            await signOut();
          } catch (err) {
            console.error('Sign out after account deletion failed', err);
          }
          navigate('/signin');
          return;
        }

        setDangerZoneSuccess(
          'All of your breathing history, reminders, and templates have been deleted.'
        );
        setReminders([]);
        setRemindersEnabled(false);
        setDisplayProfile(false);
        setDisplayName('');
        setProfileBio('');
        setProfileName('');
        setProfilePictureName(null);
        setProfilePictureGeneratedAt(null);
        setReminderError(null);
        setReminderSavedBanner(false);
        setEditingReminderId(null);
        setReminderModalOpen(false);
        resetReminderFormFields();
        await refreshReminders();
        try {
          await supabase.auth.refreshSession();
        } catch (err) {
          console.error('Session refresh failed after deleting data', err);
        }
      } catch (err: any) {
        console.error('Danger zone action failed', err);
        setDangerZoneError(
          err?.message || 'Unable to complete that action right now.'
        );
      } finally {
        setDangerZoneLoading(null);
        setDangerModal(null);
      }
    },
    [
      navigate,
      refreshReminders,
      resetReminderFormFields,
      sessionAccessToken,
      user,
    ]
  );

  return (
    <ProtectedRoute>
      <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-blue-100 via-teal-50 to-cyan-100">
        <div
          className="absolute inset-0 animate-pulse bg-gradient-to-br from-teal-200/30 via-blue-200/20 to-cyan-200/30"
          style={{ animationDuration: '8s' }}
          aria-hidden="true"
        />

        <div className="mx-auto w-full px-4 pt-16 text-center sm:max-w-5xl sm:px-0">
          <h1 className="gradient-text mb-4 pb-2 text-4xl font-bold sm:text-5xl">
            Settings
          </h1>
          <p className="text-lg text-gray-600">
            Tune how <span className="logo-font font-bold">Xhalin</span> feels
            for you—update your account details, personalize your public
            profile, and choose how often you hear from us so your breathing
            practice fits naturally into your life.
          </p>
        </div>

        <div className="relative mx-auto w-full px-2 py-6 sm:max-w-5xl sm:px-4 sm:py-12">
          <div className="animate-fade-in rounded-none bg-white/70 p-4 shadow-xl backdrop-blur sm:rounded-3xl sm:p-8">
            <div className="flex flex-col gap-6 sm:flex-row sm:items-start sm:gap-8">
              <aside className="mb-6 w-full sm:mb-0 sm:w-72">
                <div className="rounded-none bg-white/60 p-3 shadow-sm sm:rounded-2xl sm:p-4">
                  <nav aria-label="Settings navigation" className="space-y-2">
                    <button
                      type="button"
                      onClick={() => setActive('Account')}
                      className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm font-medium transition focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                        active === 'Account'
                          ? 'selected border-l-4 border-rose-500'
                          : 'hover:bg-white/50'
                      }`}
                    >
                      <span className="sr-only">Account</span>
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        className={`h-5 w-5 ${
                          active === 'Account' ? 'text-white' : 'text-gray-500'
                        }`}
                        aria-hidden="true"
                      >
                        <path
                          d="M12 12a4 4 0 100-8 4 4 0 000 8z"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M6 20a6 6 0 0112 0"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <span
                        className={`${
                          active === 'Account' ? 'text-white' : 'text-gray-800'
                        }`}
                      >
                        Account
                      </span>
                    </button>

                    <button
                      type="button"
                      onClick={() => setActive('Profile')}
                      className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm font-medium transition focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                        active === 'Profile'
                          ? 'selected border-l-4 border-rose-500'
                          : 'hover:bg-white/50'
                      }`}
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        className={`h-5 w-5 ${
                          active === 'Profile' ? 'text-white' : 'text-gray-500'
                        }`}
                        aria-hidden="true"
                      >
                        <path
                          d="M12 12a4 4 0 100-8 4 4 0 000 8z"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <span
                        className={`${
                          active === 'Profile' ? 'text-white' : 'text-gray-800'
                        }`}
                      >
                        Profile
                      </span>
                    </button>

                    <button
                      type="button"
                      onClick={() => setActive('Breathing Reminders')}
                      className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm font-medium transition focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                        active === 'Breathing Reminders'
                          ? 'selected border-l-4 border-rose-500'
                          : 'hover:bg-white/50'
                      }`}
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        className={`h-5 w-5 ${
                          active === 'Breathing Reminders'
                            ? 'text-white'
                            : 'text-gray-500'
                        }`}
                        aria-hidden="true"
                      >
                        <path
                          d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6 6 0 10-12 0v3.159c0 .538-.214 1.055-.595 1.436L4 17h5"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M13.73 21a2 2 0 01-3.46 0"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <span
                        className={`${
                          active === 'Breathing Reminders'
                            ? 'text-white'
                            : 'text-gray-800'
                        }`}
                      >
                        Breathing Reminders
                      </span>
                    </button>
                  </nav>
                </div>
              </aside>

              <main className="w-full">
                <div className="rounded-none bg-white/60 p-4 shadow-sm sm:rounded-2xl sm:p-6">
                  <div className="mb-4">
                    <h1 className="text-2xl font-extrabold text-gray-900">
                      {active} settings
                    </h1>
                    <p className="mt-1 text-sm text-gray-600">
                      Manage your {active.toLowerCase()} settings and
                      preferences.
                    </p>
                  </div>

                  {active === 'Account' && (
                    <section id="account">
                      <div className="space-y-6">
                        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
                          <h3 className="mb-2 text-lg font-bold text-black">
                            Email address
                          </h3>
                          <p className="text-sm text-gray-900">
                            {user?.email ?? 'Not signed in'}
                          </p>
                        </div>
                        {/* Change password - only show if user signed up with email/password */}
                        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
                          <h3 className="mb-2 text-lg font-bold text-black">
                            Change Password
                          </h3>
                          <p className="mb-4 text-sm text-gray-600">
                            Change your account password. This is only available
                            if you signed up with email and password.
                          </p>

                          {/* determine if password change is allowed */}
                          {(() => {
                            const sessionAny: any = (session as any) || null;
                            const canChangePassword = Boolean(
                              sessionAny?.provider === 'email' ||
                                sessionAny?.user?.identities?.some?.(
                                  (i: any) => i.provider === 'email'
                                )
                            );

                            if (!canChangePassword) {
                              return (
                                <div className="text-sm text-gray-600">
                                  Password changes are not available for
                                  accounts created via social or external
                                  providers.
                                </div>
                              );
                            }

                            return (
                              <div className="space-y-4">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700">
                                    Current password
                                  </label>
                                  <div className="mt-1 flex flex-col gap-2 sm:flex-row sm:items-center">
                                    <input
                                      type={showPasswords ? 'text' : 'password'}
                                      value={currentPassword}
                                      onChange={(e) =>
                                        setCurrentPassword(e.target.value)
                                      }
                                      placeholder="Enter your current password"
                                      className="w-full rounded border px-3 py-2 text-sm"
                                    />
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setShowPasswords((s) => !s)
                                      }
                                      className="rounded bg-gray-100 px-3 py-2 text-sm text-gray-700"
                                    >
                                      {showPasswords ? 'Hide' : 'Show'}
                                    </button>
                                  </div>
                                </div>
                                {/* remaining password inputs and save button follow */}

                                <div>
                                  <label className="block text-sm font-medium text-gray-700">
                                    New password
                                  </label>
                                  <div className="mt-1 flex flex-col gap-2 sm:flex-row sm:items-center">
                                    <input
                                      type={showPasswords ? 'text' : 'password'}
                                      value={newPassword}
                                      onChange={(e) =>
                                        setNewPassword(e.target.value)
                                      }
                                      placeholder="Enter your new password"
                                      className="w-full rounded border px-3 py-2 text-sm"
                                    />
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setShowPasswords((s) => !s)
                                      }
                                      className="rounded bg-gray-100 px-3 py-2 text-sm text-gray-700"
                                    >
                                      {showPasswords ? 'Hide' : 'Show'}
                                    </button>
                                  </div>
                                </div>

                                <div>
                                  <label className="block text-sm font-medium text-gray-700">
                                    Confirm new password
                                  </label>
                                  <div className="mt-1 flex flex-col gap-2 sm:flex-row sm:items-center">
                                    <input
                                      type={showPasswords ? 'text' : 'password'}
                                      value={confirmNewPassword}
                                      onChange={(e) =>
                                        setConfirmNewPassword(e.target.value)
                                      }
                                      placeholder="Confirm your new password"
                                      className="w-full rounded border px-3 py-2 text-sm"
                                    />
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setShowPasswords((s) => !s)
                                      }
                                      className="rounded bg-gray-100 px-3 py-2 text-sm text-gray-700"
                                    >
                                      {showPasswords ? 'Hide' : 'Show'}
                                    </button>
                                  </div>
                                </div>

                                {passwordMessage && (
                                  <p className="text-sm text-rose-600">
                                    {passwordMessage}
                                  </p>
                                )}

                                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                                  <button
                                    type="button"
                                    disabled={updatingPassword}
                                    onClick={async () => {
                                      setPasswordMessage(null);
                                      if (!user || !user.email) return;
                                      if (
                                        !currentPassword ||
                                        !newPassword ||
                                        !confirmNewPassword
                                      ) {
                                        setPasswordMessage(
                                          'Please fill in all fields.'
                                        );
                                        return;
                                      }
                                      if (newPassword !== confirmNewPassword) {
                                        setPasswordMessage(
                                          'New passwords do not match.'
                                        );
                                        return;
                                      }

                                      setUpdatingPassword(true);
                                      try {
                                        // Re-authenticate by signing in with email and current password
                                        const {
                                          data: signInData,
                                          error: signInError,
                                        } =
                                          await supabase.auth.signInWithPassword(
                                            {
                                              email: user.email,
                                              password: currentPassword,
                                            }
                                          );

                                        if (signInError || !signInData) {
                                          throw (
                                            signInError ||
                                            new Error(
                                              'Failed to verify current password'
                                            )
                                          );
                                        }

                                        // Now update the user's password
                                        const { error: updateError } =
                                          await supabase.auth.updateUser({
                                            password: newPassword,
                                          } as any);

                                        if (updateError) throw updateError;

                                        setCurrentPassword('');
                                        setNewPassword('');
                                        setConfirmNewPassword('');
                                        setPasswordMessage(
                                          'Password updated successfully.'
                                        );
                                        try {
                                          await signOut();
                                        } catch (e) {
                                          console.error(
                                            'Sign out after password update failed',
                                            e
                                          );
                                        }
                                        navigate('/signin');
                                      } catch (err: any) {
                                        setPasswordMessage(
                                          err?.message ||
                                            'Failed to update password.'
                                        );
                                      } finally {
                                        setUpdatingPassword(false);
                                      }
                                    }}
                                    className="begin-breathing-button inline-flex w-full items-center justify-center rounded-lg px-6 py-3 text-sm font-semibold transition-all focus:outline-none focus:ring-2 focus:ring-primary-500 active:scale-95 disabled:cursor-not-allowed disabled:opacity-50"
                                  >
                                    {updatingPassword
                                      ? 'Updating...'
                                      : 'Update password'}
                                  </button>
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                        <div className="rounded-xl border border-rose-200 bg-white p-6 shadow-sm">
                          <h3 className="mb-2 text-lg font-bold text-rose-600">
                            Danger zone
                          </h3>
                          <p className="mt-1 text-sm text-gray-600">
                            Account:{' '}
                            <span className="font-semibold text-gray-900">
                              {user?.email ?? 'Not signed in'}
                            </span>
                          </p>

                          <div className="mt-4 space-y-4">
                            <div className="rounded-xl border border-amber-200/70 bg-amber-50/70 p-4">
                              <div className="flex flex-col gap-1">
                                <h4 className="text-base font-semibold text-amber-900">
                                  Delete all data
                                </h4>
                                <p className="text-sm text-amber-900/80">
                                  Keep your account but erase your breathing
                                  sessions, reminders, templates, and
                                  leaderboard stats.
                                </p>
                              </div>
                              <button
                                type="button"
                                disabled={dangerZoneLoading !== null}
                                onClick={() => {
                                  setDangerZoneError(null);
                                  setDangerZoneSuccess(null);
                                  setDangerModal('delete-data');
                                }}
                                className="mt-4 flex w-full items-center justify-center gap-2 rounded-lg bg-amber-600 px-6 py-3 text-base font-semibold text-white shadow hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-70"
                              >
                                <svg
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="1.5"
                                  className="h-5 w-5"
                                  aria-hidden="true"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M9 12h6m-7 8h8a2 2 0 001.995-1.85L19 18V8H5v10a2 2 0 001.85 1.995L7 20zm3-14h4"
                                  />
                                </svg>
                                {dangerZoneLoading === 'delete-data'
                                  ? 'Deleting...'
                                  : 'Delete all data'}
                              </button>
                            </div>

                            <div className="rounded-xl border border-rose-200 bg-rose-50/70 p-4">
                              <div className="flex flex-col gap-1">
                                <h4 className="text-base font-semibold text-rose-900">
                                  Delete account
                                </h4>
                                <p className="text-sm text-rose-900/80">
                                  Permanently remove your account and all data.
                                  This is irreversible.
                                </p>
                              </div>
                              <button
                                type="button"
                                disabled={dangerZoneLoading !== null}
                                onClick={() => {
                                  setDangerZoneError(null);
                                  setDangerZoneSuccess(null);
                                  setDangerModal('delete-account');
                                }}
                                className="mt-4 flex w-full items-center justify-center gap-2 rounded-lg bg-rose-600 px-6 py-3 text-base font-semibold text-white shadow hover:bg-rose-700 focus:outline-none focus:ring-2 focus:ring-rose-400 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-70"
                              >
                                <svg
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="1.5"
                                  className="h-5 w-5"
                                  aria-hidden="true"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M9 12h6m-7 8h8a2 2 0 001.995-1.85L19 18V8H5v10a2 2 0 001.85 1.995L7 20zm3-14h4"
                                  />
                                </svg>
                                {dangerZoneLoading === 'delete-account'
                                  ? 'Deleting...'
                                  : 'Delete account'}
                              </button>
                            </div>
                          </div>

                          {dangerZoneError && (
                            <p className="mt-4 text-sm text-rose-600">
                              {dangerZoneError}
                            </p>
                          )}
                          {dangerZoneSuccess && (
                            <p className="mt-2 text-sm text-emerald-600">
                              {dangerZoneSuccess}
                            </p>
                          )}
                        </div>
                      </div>
                    </section>
                  )}

                  {active === 'Profile' && (
                    <section id="profile">
                      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
                        <div className="space-y-4">
                          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <div className="text-sm">
                              <label
                                htmlFor="displayProfileSelect"
                                className="font-medium"
                              >
                                Make your profile visible on the global
                                leaderboard.
                              </label>
                            </div>

                            <div className="sm:ml-4 sm:flex-1">
                              <select
                                id="displayProfileSelect"
                                value={displayProfile ? 'yes' : 'no'}
                                onChange={(e) =>
                                  setDisplayProfile(e.target.value === 'yes')
                                }
                                className={displayProfileSelectClasses}
                                aria-label="Make profile visible on the global leaderboard"
                              >
                                <option value="yes">Yes</option>
                                <option value="no">No</option>
                              </select>
                            </div>
                          </div>

                          {profileLinkPath && (
                            <p className="text-center text-xs text-emerald-600 sm:text-left">
                              Your profile lives at{' '}
                              <Link
                                to={profileLinkPath}
                                className="font-semibold underline decoration-emerald-300 hover:text-emerald-700"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                /profile/{trimmedProfileName}
                              </Link>
                            </p>
                          )}

                          <div className="flex flex-col gap-6 sm:flex-row sm:items-start">
                            <div className="w-full sm:w-28">
                              <div className="mx-auto mt-2 aspect-square w-full max-w-[240px] overflow-hidden rounded-full bg-gray-100 sm:h-20 sm:w-20 sm:max-w-none">
                                {profilePictureName ? (
                                  profilePictureName.startsWith('http') ? (
                                    <button
                                      type="button"
                                      onClick={() => setProfileModalOpen(true)}
                                      className="h-full w-full cursor-pointer border-none bg-transparent p-0"
                                      aria-label="View profile picture larger"
                                    >
                                      <img
                                        src={profilePictureName}
                                        alt="avatar"
                                        className="h-full w-full object-cover"
                                      />
                                    </button>
                                  ) : (
                                    <div className="flex h-full w-full items-center justify-center text-xs text-gray-600">
                                      {profilePictureName}
                                    </div>
                                  )
                                ) : (
                                  <div className="flex h-full w-full items-center justify-center text-gray-400">
                                    <span
                                      aria-hidden="true"
                                      className="text-4xl"
                                    >
                                      😮‍💨
                                    </span>
                                    <span className="sr-only">No avatar</span>
                                  </div>
                                )}
                              </div>
                              {profilePictureGeneratedAt && (
                                <p className="mt-2 text-xs text-gray-500">
                                  Generated on{' '}
                                  {new Date(
                                    profilePictureGeneratedAt
                                  ).toLocaleDateString()}
                                </p>
                              )}

                              {(!profilePictureGeneratedAt || isAdmin) && (
                                <div className="mt-3">
                                  {canUseAiProfilePicture ? (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const defaultJob = jobOptions[0] ?? '';
                                        const defaultAnimal =
                                          animalOptions[0] ?? '';
                                        setSelectedJob(defaultJob);
                                        setSelectedAnimal(defaultAnimal);
                                        setAiModalOpen(true);
                                      }}
                                      className="item-selected inline-flex w-full cursor-pointer items-center justify-center rounded bg-pink-600 px-2 py-2 text-sm font-medium text-white transition-opacity hover:bg-pink-700"
                                    >
                                      {profilePictureGeneratedAt
                                        ? 'Change AI Profile Picture'
                                        : 'Add AI Profile Picture'}
                                    </button>
                                  ) : (
                                    <div className="rounded-xl border border-amber-200 bg-amber-50/80 px-4 py-4 text-xs text-gray-700">
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setShowAiProfileLockedModal(true);
                                        }}
                                        className="item-selected inline-flex w-full cursor-pointer items-center justify-center rounded bg-pink-600 px-2 py-2 text-sm font-medium text-white transition-opacity hover:bg-pink-700"
                                        style={{ opacity: 0.85 }}
                                      >
                                        <span className="flex items-center gap-2">
                                          {profilePictureGeneratedAt
                                            ? 'Change AI Profile Picture'
                                            : 'Add AI Profile Picture'}
                                        </span>
                                      </button>
                                      <a
                                        href="#"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          setShowAiProfileLockedModal(true);
                                        }}
                                        className="mt-3 font-semibold text-primary-600 underline decoration-primary-200 hover:text-primary-700"
                                      >
                                        Tap to learn how to unlock this feature.
                                      </a>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>

                            <div className="flex-1">
                              <label className="text-sm font-medium text-gray-700">
                                Display name
                              </label>
                              <input
                                value={displayName}
                                onChange={(e) => setDisplayName(e.target.value)}
                                maxLength={50}
                                placeholder="Display name"
                                className="mt-1 w-full rounded border px-3 py-2 text-sm"
                              />
                              <p className="mt-1 text-xs text-gray-500">
                                Optional. This is shown publicly (if you make
                                your profile public). {displayName.length}/50
                                characters
                              </p>
                              <label className="mt-4 block text-sm font-medium text-gray-700">
                                Profile name
                              </label>
                              <input
                                value={profileName}
                                onChange={(e) => setProfileName(e.target.value)}
                                placeholder="Letters, numbers, or dashes"
                                maxLength={50}
                                className="mt-1 w-full rounded border px-3 py-2 text-sm"
                              />
                              <p className="mt-1 text-xs text-gray-500">
                                Letters, numbers, and dashes only (no spaces).
                                This will be used in your profile URL:{' '}
                                <code className="rounded bg-gray-100 px-1 text-xs">
                                  /profile/{profileName || 'your-name'}
                                </code>{' '}
                                — {profileName.length}/50 characters
                              </p>
                              <label className="mt-4 block text-sm font-medium text-gray-700">
                                Profile bio
                              </label>
                              {hasProfileBio ? (
                                <>
                                  <textarea
                                    value={profileBio}
                                    onChange={(e) =>
                                      setProfileBio(e.target.value)
                                    }
                                    maxLength={200}
                                    rows={4}
                                    placeholder="Share a brief description about yourself"
                                    className="mt-1 w-full rounded border px-3 py-2 text-sm"
                                  />
                                  <p className="mt-1 text-xs text-gray-500">
                                    Brief description that appears on your
                                    public profile. {profileBio.length}/200
                                    characters
                                  </p>
                                </>
                              ) : (
                                <div
                                  onClick={() =>
                                    setShowProfileBioLockedModal(true)
                                  }
                                  className="mt-2 flex w-full cursor-pointer flex-col items-center gap-2 rounded-xl border border-yellow-200 bg-yellow-50 p-4"
                                >
                                  <div className="rounded-full border bg-white px-4 py-1 text-sm font-medium">
                                    Profile Bio
                                  </div>

                                  <a
                                    href="#"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      setShowProfileBioLockedModal(true);
                                    }}
                                    className="text-sm text-blue-600 underline"
                                  >
                                    Tap to learn how to unlock this feature.
                                  </a>
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
                            <button
                              type="button"
                              onClick={async () => {
                                setError(null);
                                if (!user) return;
                                // simple client validation for profile name
                                if (
                                  profileName &&
                                  !/^[A-Za-z0-9-]+$/.test(profileName)
                                ) {
                                  setError(
                                    'Profile name can only contain letters, numbers and dashes.'
                                  );
                                  return;
                                }

                                setSaving(true);
                                let shouldReload = false;
                                try {
                                  const { data } = await supabase
                                    .from('user_settings')
                                    .select('*')
                                    .eq('user_id', user.id)
                                    .limit(1)
                                    .single();

                                  if (data) {
                                    await supabase
                                      .from('user_settings')
                                      .update({
                                        display_profile: displayProfile,
                                        display_name: displayName || null,
                                        profile_name: profileName || null,
                                        profile_bio: profileBio || null,
                                        updated_at: new Date().toISOString(),
                                      })
                                      .eq('id', data.id);
                                  } else {
                                    await supabase
                                      .from('user_settings')
                                      .insert({
                                        user_id: user.id,
                                        display_profile: displayProfile,
                                        display_name: displayName || null,
                                        profile_name: profileName || null,
                                        profile_bio: profileBio || null,
                                      });
                                  }
                                  shouldReload = true;
                                } catch (err: any) {
                                  setError(err.message || 'Failed to save');
                                } finally {
                                  setSaving(false);
                                  if (
                                    shouldReload &&
                                    typeof window !== 'undefined'
                                  ) {
                                    sessionStorage.setItem(
                                      'settings-active-tab',
                                      'Profile'
                                    );
                                    window.location.reload();
                                  }
                                }
                              }}
                              className="begin-breathing-button inline-flex flex-1 items-center justify-center rounded-lg px-6 py-3 text-lg font-semibold transition-all focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 active:scale-95 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              {saving ? 'Saving...' : 'Save profile'}
                            </button>

                            {error && (
                              <p className="text-sm text-rose-600">{error}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </section>
                  )}

                  {active === 'Breathing Reminders' && (
                    <section id="breathing-reminders">
                      <div className="space-y-6 rounded-none border-0 bg-white p-0 shadow-sm sm:rounded-xl sm:border sm:border-gray-200 sm:p-6">
                        <div className="flex flex-col gap-2 rounded-2xl bg-gray-50 p-4 sm:flex-row sm:items-center sm:justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-800">
                              Reminder Status
                            </p>
                            {!remindersEnabled && (
                              <p className="mt-1 text-xs text-gray-600">
                                Turn this on to schedule your breathing
                                sessions.
                              </p>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={handleReminderToggle}
                            className={`relative inline-flex h-8 w-16 items-center rounded-full border transition ${
                              remindersEnabled
                                ? 'border-emerald-400 bg-emerald-500'
                                : 'border-gray-300 bg-gray-200 ring-1 ring-gray-100'
                            } ${notificationSetupLoading ? 'opacity-60' : ''}`}
                            aria-pressed={remindersEnabled}
                            aria-label="Toggle breathing reminders"
                            disabled={notificationSetupLoading}
                          >
                            <span
                              className={`inline-block h-6 w-6 transform rounded-full shadow transition ${
                                remindersEnabled
                                  ? 'translate-x-8 bg-white'
                                  : 'translate-x-1 border-2 border-gray-500 bg-gray-400'
                              }`}
                            />
                            <span className="sr-only">
                              {remindersEnabled ? 'On' : 'Off'}
                            </span>
                          </button>
                        </div>
                        {notificationSetupLoading && (
                          <p className="text-xs text-gray-500">
                            Setting up push notifications...
                          </p>
                        )}
                        {!notificationSupported && (
                          <p className="text-xs text-rose-600">
                            Push notifications are not supported in this
                            browser.
                          </p>
                        )}
                        {iosNeedsPwa && (
                          <p className="text-xs text-amber-600">
                            Push reminders on iPhone/iPad only work when Xhalin
                            is added to your Home Screen.
                          </p>
                        )}
                        {notificationStatusMessage && (
                          <p className="text-xs text-amber-600">
                            {notificationStatusMessage}
                          </p>
                        )}
                        {notificationPermission === 'denied' && (
                          <p className="text-xs text-rose-600">
                            Notifications are blocked. Use the browser&apos;s
                            lock-icon menu to allow push notifications for
                            Xhalin.
                          </p>
                        )}
                        {reminderError && (
                          <p className="mt-1 text-xs text-rose-600">
                            {reminderError}
                          </p>
                        )}

                        {/* debug panels removed */}

                        <div className="space-y-4">
                          <div className="flex flex-col gap-3 rounded-2xl border border-dashed border-emerald-200 bg-white/90 p-4 sm:flex-row sm:items-center sm:justify-between">
                            <div>
                              <p className="text-sm font-medium text-gray-800">
                                Create a breathing reminder
                              </p>
                              <p className="text-xs text-gray-600">
                                Set your preferred frequency, time, and delivery
                                type.
                              </p>
                              <p className="text-xs text-gray-500">
                                Your timezone: {browserTimezone}
                              </p>
                              {!remindersEnabled && (
                                <p className="mt-1 text-xs text-amber-600">
                                  Turn on reminders to save a new schedule.
                                </p>
                              )}
                            </div>
                            <button
                              type="button"
                              disabled={!remindersEnabled}
                              aria-disabled={!remindersEnabled}
                              className="begin-breathing-button inline-flex items-center justify-center rounded-lg px-5 py-2 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                              onClick={() => {
                                clearReminderModalTimer();
                                resetReminderFormFields();
                                setReminderSavedBanner(false);
                                setReminderError(null);
                                setReminderModalOpen(true);
                              }}
                            >
                              Create Reminder
                            </button>
                          </div>

                          {remindersEnabled && reminders.length > 0 && (
                            <div className="mt-4 space-y-3 rounded-2xl bg-gray-50 p-4">
                              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                Your Active Reminders
                              </p>
                              <div className="space-y-3">
                                {reminders.map((r) => {
                                  const isToggling =
                                    togglingReminderIds.includes(r.id);
                                  return (
                                    <div
                                      key={r.id}
                                      className="flex flex-col gap-2 rounded-xl bg-white/70 p-3 sm:flex-row sm:items-center sm:justify-between"
                                    >
                                      <div>
                                        <p className="text-sm font-medium text-gray-800">
                                          {(() => {
                                            switch (r.frequency) {
                                              case 'weekly':
                                                return 'Weekly';
                                              case 'weekdays':
                                                return 'Weekdays only';
                                              case 'once':
                                                return 'Once';
                                              case 'custom': {
                                                const formatted =
                                                  formatReminderDays(r.days);
                                                return formatted
                                                  ? `On ${formatted}`
                                                  : 'Custom days';
                                              }
                                              default:
                                                return 'Daily';
                                            }
                                          })()}{' '}
                                          at{' '}
                                          {(() => {
                                            try {
                                              const [hour, minute] = (
                                                r.time || DEFAULT_REMINDER_TIME
                                              ).split(':');
                                              const date = new Date();
                                              date.setHours(
                                                Number(hour) || 0,
                                                Number(minute) || 0,
                                                0,
                                                0
                                              );
                                              return date.toLocaleTimeString(
                                                [],
                                                {
                                                  hour: 'numeric',
                                                  minute: '2-digit',
                                                }
                                              );
                                            } catch {
                                              return r.time;
                                            }
                                          })()}
                                        </p>
                                        <p className="text-sm text-gray-700">
                                          Via{' '}
                                          {r.notification_type === 'both'
                                            ? 'push notification and email'
                                            : r.notification_type === 'push'
                                              ? 'push notification'
                                              : 'email'}
                                        </p>
                                        {!r.enabled && (
                                          <p className="text-xs font-semibold text-amber-600">
                                            Paused
                                          </p>
                                        )}
                                      </div>
                                      <div className="flex flex-wrap gap-2 pt-1 sm:pt-0">
                                        <Button
                                          type="button"
                                          size="sm"
                                          disabled={isToggling}
                                          onClick={() =>
                                            toggleReminderEnabled(
                                              r.id,
                                              r.enabled
                                            )
                                          }
                                          className={`${
                                            r.enabled
                                              ? '!bg-amber-500 !text-white hover:!bg-amber-600 focus:!ring-amber-400'
                                              : '!bg-emerald-500 !text-white hover:!bg-emerald-600 focus:!ring-emerald-400'
                                          }`}
                                        >
                                          {isToggling
                                            ? r.enabled
                                              ? 'Pausing...'
                                              : 'Resuming...'
                                            : r.enabled
                                              ? 'Pause'
                                              : 'Resume'}
                                        </Button>
                                        <Button
                                          type="button"
                                          size="sm"
                                          onClick={() => {
                                            setRemindersEnabled(true);
                                            setEditingReminderId(r.id);
                                            setEditingReminderEnabled(
                                              r.enabled
                                            );
                                            if (r.frequency === 'once') {
                                              setIsRepeat(false);
                                              setReminderDays([]);
                                            } else {
                                              setIsRepeat(true);
                                              if (r.frequency === 'daily') {
                                                setReminderDays([
                                                  ...DEFAULT_REPEAT_DAYS,
                                                ]);
                                              } else if (
                                                r.frequency === 'weekdays'
                                              ) {
                                                setReminderDays([
                                                  ...WEEKDAY_VALUES,
                                                ]);
                                              } else {
                                                setReminderDays(
                                                  normalizeDaySelection(
                                                    r.days || []
                                                  )
                                                );
                                              }
                                            }
                                            setReminderTime(
                                              r.time || DEFAULT_REMINDER_TIME
                                            );
                                            const raw = (
                                              r.notification_type || 'push'
                                            )
                                              .toString()
                                              .toLowerCase();
                                            const normalized =
                                              raw === 'email' || raw === 'both'
                                                ? raw
                                                : 'push';
                                            setReminderNotificationType(
                                              normalized as
                                                | 'push'
                                                | 'email'
                                                | 'both'
                                            );
                                            setReminderMessage(
                                              r.message ||
                                                DEFAULT_REMINDER_MESSAGE
                                            );
                                            setReminderTimezone(
                                              browserTimezone
                                            );
                                            clearReminderModalTimer();
                                            setReminderSavedBanner(false);
                                            setReminderError(null);
                                            setReminderModalOpen(true);
                                          }}
                                          className="!bg-sky-500 !text-white hover:!bg-sky-600 focus:!ring-sky-400"
                                        >
                                          Edit
                                        </Button>

                                        <Button
                                          type="button"
                                          size="sm"
                                          onClick={() => {
                                            setReminderError(null);
                                            if (!user) return;
                                            setDeleteTargetId(r.id);
                                            setDeleteConfirmOpen(true);
                                          }}
                                          className="!bg-rose-500 !text-white hover:!bg-rose-600 focus:!ring-rose-400"
                                        >
                                          Delete
                                        </Button>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </section>
                  )}
                </div>
              </main>
            </div>
          </div>
        </div>
        {reminderModalOpen && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4 py-6"
            role="dialog"
            aria-modal="true"
            aria-label={
              editingReminderId
                ? 'Edit breathing reminder'
                : 'Create breathing reminder'
            }
            onClick={closeReminderModal}
          >
            <div
              className="relative w-full max-w-2xl rounded-2xl bg-white p-6 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                aria-label="Close reminder modal"
                className="absolute right-4 top-4 rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-600 hover:bg-gray-200"
                onClick={closeReminderModal}
              >
                Close
              </button>
              <div className="mt-4 max-h-[80vh] space-y-6 overflow-y-auto pr-1">
                <div className="space-y-1 pr-6">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {editingReminderId ? 'Edit Reminder' : 'Create Reminder'}
                  </h3>
                  <p className="text-sm text-gray-600">
                    Choose your frequency, timing, and notification preferences.
                  </p>
                </div>

                <div className="space-y-6">
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Time of day
                      </label>
                      <input
                        type="time"
                        value={reminderTime}
                        onChange={(e) => setReminderTime(e.target.value)}
                        className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                      />
                      <p className="text-xs text-gray-500">
                        Detected timezone: {reminderTimezone}
                      </p>
                    </div>

                    <div className="space-y-2">
                      <div className="mb-2 flex items-center gap-2">
                        <div className="relative flex items-center">
                          <input
                            type="checkbox"
                            id="repeat-checkbox"
                            checked={isRepeat}
                            onChange={(e) => setIsRepeat(e.target.checked)}
                            className="h-5 w-5 rounded border-gray-300 text-emerald-500 focus:ring-emerald-400"
                          />
                          <label
                            htmlFor="repeat-checkbox"
                            className="ml-2 text-sm font-medium text-gray-700"
                          >
                            Repeat
                          </label>
                        </div>
                      </div>

                      <div
                        className={`transition-opacity ${
                          isRepeat
                            ? 'opacity-100'
                            : 'pointer-events-none opacity-50'
                        }`}
                      >
                        <div className="flex flex-wrap gap-2">
                          {DAYS.map((day) => {
                            const isSelected = reminderDays.includes(day.value);
                            return (
                              <label
                                key={day.value}
                                title={day.full}
                                className={`inline-flex h-10 w-10 items-center justify-center rounded-full border text-sm font-semibold transition ${
                                  isSelected
                                    ? 'item-selected border-transparent shadow-sm'
                                    : 'border-gray-200 bg-white text-gray-600 hover:border-emerald-300 hover:text-emerald-700'
                                } ${isRepeat ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}
                              >
                                <input
                                  type="checkbox"
                                  className="sr-only"
                                  checked={isSelected}
                                  aria-label={day.full}
                                  onChange={() => toggleReminderDay(day.value)}
                                  disabled={!isRepeat}
                                />
                                <span>{day.label}</span>
                              </label>
                            );
                          })}
                        </div>
                        <p className="mt-2 text-xs text-gray-500">
                          {reminderDays.length > 0
                            ? `Selected: ${formatReminderDays(reminderDays)}`
                            : 'No days selected'}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <p className="text-sm font-medium text-gray-700">
                      Notification type
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {[
                        { value: 'push', label: 'Push notification' },
                        { value: 'email', label: 'Email' },
                        { value: 'both', label: 'Both' },
                      ].map((option) => {
                        const isActive =
                          reminderNotificationType === option.value;
                        return (
                          <button
                            key={option.value}
                            type="button"
                            onClick={() =>
                              setReminderNotificationType(
                                option.value as 'push' | 'email' | 'both'
                              )
                            }
                            className={`rounded-full px-4 py-2 text-sm font-medium transition ${
                              isActive
                                ? 'item-selected bg-emerald-500 text-white shadow-sm'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            {option.label}
                          </button>
                        );
                      })}
                    </div>
                    {(reminderNotificationType === 'push' ||
                      reminderNotificationType === 'both') &&
                      (!notificationSupported ||
                        notificationPermission !== 'granted') && (
                        <p className="mt-1 text-xs text-amber-600">
                          To receive push reminders, please allow notifications
                          in your browser or phone.
                        </p>
                      )}
                  </div>

                  <div className="space-y-2 rounded-2xl bg-gradient-to-r from-emerald-50 via-sky-50 to-cyan-50 p-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-emerald-600">
                      Preview
                    </p>
                    <p className="text-sm text-gray-800">{reminderMessage}</p>
                  </div>

                  <div className="space-y-3 pb-2">
                    <button
                      type="button"
                      disabled={reminderSaving || reminderSavedBanner}
                      onClick={async () => {
                        if (reminderSaving || reminderSavedBanner) return;
                        setReminderError(null);
                        setReminderSavedBanner(false);
                        if (!user) return;

                        if (!remindersEnabled) {
                          setReminderError('Turn on reminders to save one.');
                          return;
                        }

                        if (isRepeat && reminderDays.length === 0) {
                          setReminderError(
                            'Pick at least one day for a repeating schedule.'
                          );
                          return;
                        }

                        const needsPush =
                          reminderNotificationType === 'push' ||
                          reminderNotificationType === 'both';

                        if (needsPush) {
                          const granted = await ensurePushPermission();
                          if (!granted) {
                            return;
                          }
                        }

                        setReminderSaving(true);
                        try {
                          let frequency = 'once';
                          let days: string[] | null = null;
                          const normalizedDays =
                            normalizeDaySelection(reminderDays);

                          if (isRepeat) {
                            if (hasSameMembers(normalizedDays, DAY_VALUES)) {
                              frequency = 'daily';
                              days = null;
                            } else if (
                              hasSameMembers(normalizedDays, WEEKDAY_VALUES)
                            ) {
                              frequency = 'weekdays';
                              days = null;
                            } else {
                              frequency = 'custom';
                              days = normalizedDays;
                            }
                          }

                          const payload: Record<string, any> = {
                            user_id: user.id,
                            enabled: editingReminderId
                              ? editingReminderEnabled
                              : true,
                            frequency,
                            days,
                            time: reminderTime || DEFAULT_REMINDER_TIME,
                            notification_type: reminderNotificationType,
                            message: reminderMessage,
                            timezone:
                              reminderTimezone ||
                              browserTimezone ||
                              defaultTimezone,
                            updated_at: new Date().toISOString(),
                          };

                          if (editingReminderId) {
                            await supabase
                              .from('breathing_reminders')
                              .update(payload)
                              .eq('id', editingReminderId);
                          } else {
                            await supabase
                              .from('breathing_reminders')
                              .insert(payload);
                          }

                          setReminderSavedBanner(true);
                          setEditingReminderId(null);

                          await refreshReminders();
                          clearReminderModalTimer();
                          if (typeof window !== 'undefined') {
                            reminderModalCloseTimeout.current =
                              window.setTimeout(() => {
                                closeReminderModal();
                              }, 1200);
                          } else {
                            closeReminderModal();
                          }
                        } catch (err: any) {
                          setReminderError(
                            err?.message || 'Failed to save reminder'
                          );
                        } finally {
                          setReminderSaving(false);
                        }
                      }}
                      className="begin-breathing-button inline-flex w-full items-center justify-center rounded-lg px-6 py-3 text-lg font-semibold focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {reminderSaving
                        ? editingReminderId
                          ? 'Updating reminder...'
                          : 'Saving reminder...'
                        : editingReminderId
                          ? 'Update Reminder'
                          : 'Save Reminder'}
                    </button>
                    {reminderSavedBanner && (
                      <div className="rounded-lg bg-emerald-50 px-3 py-2 text-center text-sm text-emerald-700">
                        Your reminder is set.
                      </div>
                    )}
                    {reminderError && (
                      <p className="text-sm text-rose-600">{reminderError}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {aiModalOpen && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center px-4"
            role="dialog"
            aria-modal="true"
          >
            <div
              className="fixed inset-0 bg-black/40"
              onClick={() => setAiModalOpen(false)}
            />

            <div className="z-50 max-w-lg rounded-2xl bg-white p-6 shadow-lg">
              <h3 className="text-lg font-semibold text-gray-900">
                AI profile picture generator
              </h3>
              <p className="mt-2 text-sm text-gray-600">
                Create a unique AI-generated profile picture by selecting a job
                and an animal.
              </p>

              <div className="mt-4 space-y-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Select a job
                  </label>
                  <select
                    value={selectedJob}
                    onChange={(e) => setSelectedJob(e.target.value)}
                    className="mt-1 w-full rounded border px-3 py-2 text-sm"
                  >
                    <option value="">Choose a profession...</option>
                    {jobOptions.map((j) => (
                      <option key={j} value={j}>
                        {j}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Select an animal
                  </label>
                  <select
                    value={selectedAnimal}
                    onChange={(e) => setSelectedAnimal(e.target.value)}
                    className="mt-1 w-full rounded border px-3 py-2 text-sm"
                  >
                    <option value="">Choose an animal...</option>
                    {animalOptions.map((a) => (
                      <option key={a} value={a}>
                        {a}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
                <button
                  type="button"
                  onClick={() => setAiModalOpen(false)}
                  className="rounded bg-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-300"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  onClick={async () => {
                    setError(null);
                    if (!selectedJob || !selectedAnimal) return;
                    if (!user) {
                      setError('You must be signed in to generate an avatar.');
                      return;
                    }
                    if (!canUseAiProfilePicture) {
                      setError('Feature locked');
                      return;
                    }

                    setGeneratingAvatar(true);
                    try {
                      const resp = await fetch(
                        'https://n8n.srv895748.hstgr.cloud/webhook/create-ai-avatar',
                        {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json',
                          },
                          body: JSON.stringify({
                            job: selectedJob,
                            animal: selectedAnimal,
                            userId: user.id,
                            displayName: displayName || '',
                            profileName: profileName || '',
                          }),
                        }
                      );

                      if (!resp.ok) {
                        const text = await resp.text();
                        throw new Error(text || 'Webhook request failed');
                      }

                      const payload = await resp.json().catch(() => null);
                      const storageKeyCandidate = Array.isArray(payload)
                        ? payload[0]?.Key || payload[0]?.key
                        : payload?.Key || payload?.key;

                      if (
                        !storageKeyCandidate ||
                        typeof storageKeyCandidate !== 'string'
                      ) {
                        throw new Error(
                          'Webhook response missing storage key.'
                        );
                      }

                      const supabaseUrl = (
                        import.meta.env.VITE_SUPABASE_URL || ''
                      ).replace(/\/$/, '');
                      const normalizedKey = storageKeyCandidate.replace(
                        /^\/+/,
                        ''
                      );
                      const pictureUrl = `${supabaseUrl}/storage/v1/object/public/${encodeURI(
                        normalizedKey
                      )}`;
                      const generatedAt = new Date().toISOString();

                      setProfilePictureName(pictureUrl);
                      setProfilePictureGeneratedAt(generatedAt);

                      const updatePayload: Record<
                        string,
                        string | boolean | null
                      > = {
                        profile_picture_name: pictureUrl,
                        profile_picture_generated_at: generatedAt,
                        updated_at: generatedAt,
                      };

                      const { data: updatedRows, error: updateError } =
                        await supabase
                          .from('user_settings')
                          .update(updatePayload)
                          .eq('user_id', user.id)
                          .select('id')
                          .limit(1);

                      if (updateError) {
                        throw updateError;
                      }

                      if (!updatedRows || updatedRows.length === 0) {
                        const { error: insertError } = await supabase
                          .from('user_settings')
                          .insert({
                            user_id: user.id,
                            display_profile: displayProfile,
                            display_name: displayName || null,
                            profile_name: profileName || null,
                            ...updatePayload,
                          })
                          .select('id')
                          .single();

                        if (insertError) {
                          throw insertError;
                        }
                      }
                      setAiModalOpen(false);
                    } catch (err: any) {
                      setError(err?.message || 'Failed to request AI avatar');
                    } finally {
                      setGeneratingAvatar(false);
                    }
                  }}
                  className="rounded bg-pink-600 px-4 py-2 text-sm font-medium text-white hover:bg-pink-700"
                >
                  {generatingAvatar ? 'Generating...' : 'Generate AI avatar'}
                </button>
                {generatingAvatar && (
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-700">
                    This could take up to a minute. Please wait.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
        {showAiProfileLockedModal &&
          (() => {
            const single =
              aiProfileBadgeRequirements.length === 1
                ? aiProfileBadgeRequirements[0]
                : null;

            return (
              <Modal
                title="Unlock AI Profile Pictures"
                onClose={() => setShowAiProfileLockedModal(false)}
                className="max-w-xl"
                {...(single
                  ? {
                      style: { backgroundColor: single.color || undefined },
                      textColorClass: single.color ? 'text-white' : undefined,
                      closeButtonClass: single.color
                        ? 'text-white transition hover:bg-white/10 hover:text-white'
                        : undefined,
                    }
                  : undefined)}
              >
                {single ? (
                  <div className="flex justify-center">
                    <div className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-lg">
                      <div className="flex items-center gap-6 sm:gap-10">
                        <div
                          role="button"
                          tabIndex={0}
                          onClick={() => setPreviewBadge(single)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setPreviewBadge(single);
                            }
                          }}
                          className="h-56 w-56 flex-shrink-0 cursor-pointer overflow-hidden rounded-full border border-gray-100 shadow-inner"
                          style={{ backgroundColor: single.color ?? undefined }}
                        >
                          {single.image_url ? (
                            <img
                              src={
                                buildBadgeAssetUrl(single.image_url) ||
                                undefined
                              }
                              alt={single.name || 'badge'}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <div className="flex h-full items-center justify-center text-3xl">
                              {single.name?.slice(0, 1) ?? '🏅'}
                            </div>
                          )}
                        </div>

                        <div className="flex-1 text-left">
                          {single.name && (
                            <div className="text-2xl font-semibold text-gray-900">
                              {single.name}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="mt-6 text-sm text-gray-700">
                        <p>{aiProfileUnlockMessage}</p>
                        <p className="mt-2">
                          This feature will enable AI-generated profile avatars
                          once earned.
                        </p>

                        <div className="mt-6">
                          <Button
                            type="button"
                            className="item-selected w-full"
                            onClick={() => {
                              setShowAiProfileLockedModal(false);
                              navigate('/badges');
                            }}
                          >
                            View All Badges
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : aiProfileBadgeRequirements.length > 0 ? (
                  <div className="flex justify-center gap-3">
                    {aiProfileBadgeRequirements.map((badge) => (
                      <div
                        key={badge.id || badge.name}
                        className="flex flex-col items-center"
                      >
                        <div
                          role="button"
                          tabIndex={0}
                          onClick={() => setPreviewBadge(badge)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setPreviewBadge(badge);
                            }
                          }}
                          className="h-48 w-48 cursor-pointer overflow-hidden rounded-full focus:outline-none focus:ring-2"
                          style={{ backgroundColor: badge.color ?? undefined }}
                        >
                          {badge.image_url ? (
                            <img
                              src={
                                buildBadgeAssetUrl(badge.image_url) || undefined
                              }
                              alt={badge.name || 'badge'}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <div className="flex h-full items-center justify-center text-xl">
                              {badge.name?.slice(0, 1) ?? '🏅'}
                            </div>
                          )}
                        </div>
                        {badge.name && (
                          <div
                            className="mt-2 text-xs font-semibold"
                            style={{ color: badge.color ? '#fff' : undefined }}
                          >
                            {badge.name}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : null}

                {!single && (
                  <>
                    <p className="text-sm text-gray-600">
                      {aiProfileUnlockMessage}
                    </p>
                    <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-end">
                      <Button
                        type="button"
                        variant="secondary"
                        className="w-full sm:w-auto"
                        onClick={() => setShowAiProfileLockedModal(false)}
                      >
                        Close
                      </Button>
                      <Button
                        type="button"
                        className="item-selected w-full sm:w-auto"
                        onClick={() => {
                          setShowAiProfileLockedModal(false);
                          navigate('/badges');
                        }}
                      >
                        View All Badges
                      </Button>
                    </div>
                  </>
                )}
              </Modal>
            );
          })()}
        {showProfileBioLockedModal &&
          (() => {
            const single =
              profileBioBadgeRequirements.length === 1
                ? profileBioBadgeRequirements[0]
                : null;

            return (
              <Modal
                title="Unlock Profile Bio"
                onClose={() => setShowProfileBioLockedModal(false)}
                className="max-w-xl"
                {...(single
                  ? {
                      style: { backgroundColor: single.color || undefined },
                      textColorClass: single.color ? 'text-white' : undefined,
                      closeButtonClass: single.color
                        ? 'text-white transition hover:bg-white/10 hover:text-white'
                        : undefined,
                    }
                  : undefined)}
              >
                {single ? (
                  <div className="flex justify-center">
                    <div className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-lg">
                      <div className="flex items-center gap-6 sm:gap-10">
                        <div
                          role="button"
                          tabIndex={0}
                          onClick={() => setPreviewBadge(single)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setPreviewBadge(single);
                            }
                          }}
                          className="h-56 w-56 flex-shrink-0 cursor-pointer overflow-hidden rounded-full border border-gray-100 shadow-inner"
                          style={{ backgroundColor: single.color ?? undefined }}
                        >
                          {single.image_url ? (
                            <img
                              src={
                                buildBadgeAssetUrl(single.image_url) ||
                                undefined
                              }
                              alt={single.name || 'badge'}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <div className="flex h-full items-center justify-center text-3xl">
                              {single.name?.slice(0, 1) ?? '🏅'}
                            </div>
                          )}
                        </div>

                        <div className="flex-1 text-left">
                          {single.name && (
                            <div className="text-2xl font-semibold text-gray-900">
                              {single.name}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="mt-6 text-sm text-gray-700">
                        <p>{profileBioUnlockMessage}</p>

                        <div className="mt-6">
                          <Button
                            type="button"
                            className="item-selected w-full"
                            onClick={() => {
                              setShowProfileBioLockedModal(false);
                              navigate('/badges');
                            }}
                          >
                            View All Badges
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : profileBioBadgeRequirements.length > 0 ? (
                  <div className="flex justify-center gap-3">
                    {profileBioBadgeRequirements.map((badge) => (
                      <div
                        key={badge.id || badge.name}
                        className="flex flex-col items-center"
                      >
                        <div
                          role="button"
                          tabIndex={0}
                          onClick={() => setPreviewBadge(badge)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setPreviewBadge(badge);
                            }
                          }}
                          className="h-48 w-48 cursor-pointer overflow-hidden rounded-full focus:outline-none focus:ring-2"
                          style={{ backgroundColor: badge.color ?? undefined }}
                        >
                          {badge.image_url ? (
                            <img
                              src={
                                buildBadgeAssetUrl(badge.image_url) || undefined
                              }
                              alt={badge.name || 'badge'}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <div className="flex h-full items-center justify-center text-xl">
                              {badge.name?.slice(0, 1) ?? '🏅'}
                            </div>
                          )}
                        </div>
                        {badge.name && (
                          <div
                            className="mt-2 text-xs font-semibold"
                            style={{ color: badge.color ? '#fff' : undefined }}
                          >
                            {badge.name}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : null}

                {!single && (
                  <>
                    <p className="text-sm text-gray-600">
                      {profileBioUnlockMessage}
                    </p>
                    <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-end">
                      <Button
                        type="button"
                        variant="secondary"
                        className="w-full sm:w-auto"
                        onClick={() => setShowProfileBioLockedModal(false)}
                      >
                        Close
                      </Button>
                      <Button
                        type="button"
                        className="item-selected w-full sm:w-auto"
                        onClick={() => {
                          setShowProfileBioLockedModal(false);
                          navigate('/badges');
                        }}
                      >
                        View All Badges
                      </Button>
                    </div>
                  </>
                )}
              </Modal>
            );
          })()}
        {previewBadge && (
          <Modal
            title={previewBadge.name ?? undefined}
            onClose={() => setPreviewBadge(null)}
            className="max-w-xl"
            style={{ backgroundColor: previewBadge.color || undefined }}
            textColorClass={previewBadge.color ? 'text-white' : 'text-gray-900'}
            closeButtonClass={
              previewBadge.color
                ? 'text-white transition hover:bg-white/10 hover:text-white'
                : undefined
            }
          >
            <div className="flex justify-center">
              <div className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-lg">
                <div
                  className={`flex items-center gap-6 ${previewBadge.color ? 'text-white' : 'text-gray-900'}`}
                >
                  <div
                    className="h-56 w-56 flex-shrink-0 overflow-hidden rounded-full border border-gray-100 shadow-inner"
                    style={{ backgroundColor: previewBadge.color ?? undefined }}
                  >
                    {previewBadge.image_url ? (
                      <img
                        src={
                          buildBadgeAssetUrl(previewBadge.image_url) ||
                          undefined
                        }
                        alt={previewBadge.name || 'badge'}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-full items-center justify-center text-3xl">
                        {previewBadge.name?.slice(0, 1) ?? '🏅'}
                      </div>
                    )}
                  </div>

                  <div className="flex-1 text-left">
                    {previewBadge.name && (
                      <div className="text-2xl font-semibold text-gray-900">
                        {previewBadge.name}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </Modal>
        )}
        {profileModalOpen && profilePictureName?.startsWith('http') && (
          <div
            className="fixed inset-0 z-40 flex items-center justify-center bg-black/70 px-4 py-6"
            role="dialog"
            aria-modal="true"
            aria-label="Enlarged profile picture"
            onClick={() => setProfileModalOpen(false)}
          >
            <div
              className="relative w-full max-w-3xl"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className="absolute right-0 top-0 m-2 rounded-full bg-white/90 px-3 py-1 text-xs font-semibold text-gray-700 shadow"
                onClick={() => setProfileModalOpen(false)}
              >
                Close
              </button>
              <div className="overflow-hidden rounded-2xl bg-white shadow-2xl">
                <img
                  src={profilePictureName}
                  alt="Enlarged avatar"
                  className="h-[70vh] max-h-[80vh] w-full object-contain"
                />
              </div>
            </div>
          </div>
        )}
        {dangerModal === 'delete-data' && (
          <Modal
            title="Delete all data"
            onClose={() => {
              if (dangerZoneLoading !== 'delete-data') {
                setDangerModal(null);
              }
            }}
          >
            <div className="space-y-4">
              <p className="text-sm text-gray-700">
                This will permanently remove your breathing sessions, reminders,
                templates, and leaderboard stats. Your login will remain active.
              </p>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    if (dangerZoneLoading === 'delete-data') return;
                    setDangerModal(null);
                  }}
                  className="rounded bg-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={dangerZoneLoading === 'delete-data'}
                  onClick={() => handleDangerZoneAction('delete-data')}
                  className="rounded bg-amber-600 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {dangerZoneLoading === 'delete-data'
                    ? 'Deleting...'
                    : 'Delete all data'}
                </button>
              </div>
            </div>
          </Modal>
        )}
        {dangerModal === 'delete-account' && (
          <Modal
            title="Delete account"
            onClose={() => {
              if (dangerZoneLoading !== 'delete-account') {
                setDangerModal(null);
              }
            }}
          >
            <div className="space-y-4">
              <p className="text-sm text-gray-700">
                This will permanently delete your account, profile, and every
                piece of stored data. There is no way to undo this.
              </p>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    if (dangerZoneLoading === 'delete-account') return;
                    setDangerModal(null);
                  }}
                  className="rounded bg-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={dangerZoneLoading === 'delete-account'}
                  onClick={() => handleDangerZoneAction('delete-account')}
                  className="rounded bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {dangerZoneLoading === 'delete-account'
                    ? 'Deleting...'
                    : 'Delete account'}
                </button>
              </div>
            </div>
          </Modal>
        )}
        {deleteConfirmOpen && (
          <Modal
            title="Delete reminder"
            onClose={() => {
              setDeleteConfirmOpen(false);
              setDeleteTargetId(null);
            }}
          >
            <div className="space-y-4">
              <p className="text-sm text-gray-700">
                Are you sure you want to delete this reminder? This action
                cannot be undone.
              </p>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setDeleteConfirmOpen(false);
                    setDeleteTargetId(null);
                  }}
                  className="rounded bg-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-300"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  onClick={async () => {
                    const id = deleteTargetId;
                    setDeleteConfirmOpen(false);
                    setDeleteTargetId(null);
                    if (!user || !id) return;

                    setReminderSaving(true);
                    try {
                      await supabase
                        .from('breathing_reminders')
                        .delete()
                        .eq('id', id)
                        .eq('user_id', user.id);

                      await refreshReminders();
                      if (editingReminderId && editingReminderId === id) {
                        setEditingReminderId(null);
                      }
                    } catch (err: any) {
                      setReminderError(
                        err?.message || 'Failed to delete reminder'
                      );
                    } finally {
                      setReminderSaving(false);
                    }
                  }}
                  className="rounded bg-rose-600 px-4 py-2 text-sm font-medium text-white hover:bg-rose-700"
                >
                  Delete
                </button>
              </div>
            </div>
          </Modal>
        )}
      </div>
    </ProtectedRoute>
  );
}
